import {
  AlertCircle,
  Bell,
  CheckCircle,
  CheckSquare,
  Clock,
  MessageSquare,
  MoreHorizontal,
  TrendingUp,
  UserPlus,
  Users,
  X,
  XCircle,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { motion } from "framer-motion";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { formatRelativeDate } from "@/lib/formatters";
import { rowAnim, rowTransition } from "@/components/ui/motion";
import type { NotificationRead } from "@/api/types";

interface TypeStyle {
  icon: LucideIcon;
  bg: string;
  text: string;
  /** Used by the larger non-compact rendering for the icon disc. */
  bgSolid: string;
}

const TYPE_STYLES: Record<string, TypeStyle> = {
  lead_replied: {
    icon: MessageSquare,
    bg: "bg-emerald-50",
    text: "text-emerald-600",
    bgSolid: "bg-emerald-100",
  },
  lead_bounced: {
    icon: AlertCircle,
    bg: "bg-rose-50",
    text: "text-rose-600",
    bgSolid: "bg-rose-100",
  },
  lead_assigned: {
    icon: UserPlus,
    bg: "bg-indigo-50",
    text: "text-indigo-600",
    bgSolid: "bg-indigo-100",
  },
  // A salesperson on trial has been handed their whole allowance of numbers.
  call_limit_reached: {
    icon: AlertCircle,
    bg: "bg-amber-50",
    text: "text-amber-600",
    bgSolid: "bg-amber-100",
  },
  job_failed: {
    icon: XCircle,
    bg: "bg-rose-50",
    text: "text-rose-600",
    bgSolid: "bg-rose-100",
  },
  job_completed: {
    icon: CheckCircle,
    bg: "bg-emerald-50",
    text: "text-emerald-600",
    bgSolid: "bg-emerald-100",
  },
  score_changed: {
    icon: TrendingUp,
    bg: "bg-amber-50",
    text: "text-amber-600",
    bgSolid: "bg-amber-100",
  },
  task_assigned: {
    icon: CheckSquare,
    bg: "bg-indigo-50",
    text: "text-indigo-600",
    bgSolid: "bg-indigo-100",
  },
  task_overdue: {
    icon: Clock,
    bg: "bg-rose-50",
    text: "text-rose-600",
    bgSolid: "bg-rose-100",
  },
  member_invited: {
    icon: Users,
    bg: "bg-violet-50",
    text: "text-violet-600",
    bgSolid: "bg-violet-100",
  },
};

const DEFAULT_STYLE: TypeStyle = {
  icon: Bell,
  bg: "bg-slate-100",
  text: "text-slate-500",
  bgSolid: "bg-slate-100",
};

interface NotificationItemProps {
  notification: NotificationRead;
  onClick: () => void;
  /** Compact = bell-popover variant: no checkbox, smaller paddings, X button. */
  compact?: boolean;
  /** Non-compact only: row checkbox state + handler. */
  selected?: boolean;
  onSelectChange?: (selected: boolean) => void;
  onToggleRead?: () => void;
  onDelete: (e?: React.MouseEvent) => void;
}

export function NotificationItem({
  notification,
  onClick,
  compact = false,
  selected = false,
  onSelectChange,
  onToggleRead,
  onDelete,
}: NotificationItemProps) {
  const style = TYPE_STYLES[notification.type] ?? DEFAULT_STYLE;
  const Icon = style.icon;
  const projectName = stringFromData(notification.data, "project_name");
  const unread = !notification.is_read;

  if (compact) {
    return (
      <motion.li
        layout
        initial={rowAnim.initial}
        animate={rowAnim.animate}
        exit={rowAnim.exit}
        transition={rowTransition}
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick();
          }
        }}
        className={cn(
          "group relative flex cursor-pointer items-start gap-3 border-b border-slate-900/[0.06] px-4 py-3 transition-colors duration-150 hover:bg-indigo-500/[0.04]",
          unread && "bg-indigo-50/40",
        )}
      >
        {unread && (
          <span className="absolute left-1.5 top-5 h-2 w-2 rounded-full bg-indigo-500" />
        )}
        <span
          className={cn(
            "ml-2 flex h-7 w-7 shrink-0 items-center justify-center rounded-full",
            style.bg,
          )}
        >
          <Icon className={cn("h-4 w-4", style.text)} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-slate-900">
            {notification.title}
          </div>
          <p className="line-clamp-2 text-[13px] text-slate-500">
            {notification.message}
          </p>
          <div className="mt-1 flex items-center gap-2 text-[11px] text-slate-400">
            <span>{formatRelativeDate(notification.created_at)}</span>
            {projectName && (
              <>
                <span className="text-slate-300">·</span>
                <span className="italic">{projectName}</span>
              </>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(e);
          }}
          aria-label="Dismiss notification"
          className="absolute right-2 top-2 rounded p-1 text-slate-300 opacity-0 transition-opacity hover:bg-slate-200 hover:text-slate-700 group-hover:opacity-100 focus:opacity-100"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </motion.li>
    );
  }

  // Full-page row.
  return (
    <motion.li
      layout
      initial={rowAnim.initial}
      animate={rowAnim.animate}
      exit={rowAnim.exit}
      transition={rowTransition}
      className={cn(
        "group relative flex items-start gap-4 border-b border-slate-900/[0.06] px-6 py-4 transition-colors duration-150 hover:bg-indigo-500/[0.04]",
        unread ? "bg-indigo-50/40" : "bg-white",
      )}
    >
      {onSelectChange && (
        <div onClick={(e) => e.stopPropagation()} className="pt-1.5">
          <Checkbox
            checked={selected}
            onCheckedChange={(v) => onSelectChange(v === true)}
            aria-label={`Select ${notification.title}`}
          />
        </div>
      )}
      <button
        type="button"
        onClick={onClick}
        className="flex flex-1 items-start gap-4 text-left"
      >
        <span
          className={cn(
            "flex h-9 w-9 shrink-0 items-center justify-center rounded-full",
            style.bgSolid,
          )}
        >
          <Icon className={cn("h-4 w-4", style.text)} />
        </span>
        <div className="min-w-0 flex-1">
          <div
            className={cn(
              "truncate text-sm font-semibold",
              unread ? "text-slate-900" : "text-slate-500",
            )}
          >
            {notification.title}
          </div>
          <p className="line-clamp-2 text-[13px] text-slate-500">
            {notification.message}
          </p>
          <div className="mt-1.5 flex items-center gap-2 text-xs text-slate-400">
            <span>{formatRelativeDate(notification.created_at)}</span>
            {projectName && (
              <>
                <span className="text-slate-300">·</span>
                <span className="rounded-full border border-slate-200 bg-white px-1.5 py-0 text-[11px] font-medium text-slate-600">
                  {projectName}
                </span>
              </>
            )}
          </div>
        </div>
      </button>
      {unread && (
        <span
          aria-label="Unread"
          className="mt-2 h-2 w-2 shrink-0 rounded-full bg-indigo-500"
        />
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="rounded-md p-1.5 text-slate-400 opacity-0 transition-opacity hover:bg-slate-100 hover:text-slate-700 focus-visible:opacity-100 group-hover:opacity-100"
            aria-label="More actions"
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44">
          {onToggleRead && (
            <DropdownMenuItem onSelect={onToggleRead}>
              {unread ? "Mark as read" : "Mark as unread"}
            </DropdownMenuItem>
          )}
          {onToggleRead && <DropdownMenuSeparator />}
          <DropdownMenuItem
            onSelect={() => onDelete()}
            className="text-rose-600 focus:bg-rose-50 focus:text-rose-700"
          >
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </motion.li>
  );
}

export function destinationFor(n: NotificationRead): string | null {
  const slug = stringFromData(n.data, "project_slug");
  const companyId = numberFromData(n.data, "company_id");
  switch (n.type) {
    case "lead_replied":
    case "lead_bounced":
    case "lead_assigned":
    case "score_changed":
      if (slug && companyId != null)
        return `/projects/${slug}/companies/${companyId}`;
      return null;
    case "job_failed":
    case "job_completed":
      if (slug) return `/projects/${slug}/engine`;
      return null;
    case "task_assigned":
    case "task_overdue":
      if (slug) return `/projects/${slug}/tasks`;
      return null;
    case "member_invited":
      return "/projects";
    case "call_limit_reached":
      if (slug) return `/projects/${slug}/settings`;
      return null;
    default:
      if (slug) return `/projects/${slug}/dashboard`;
      return null;
  }
}

export const NOTIFICATION_CATEGORIES = {
  leads: ["lead_replied", "lead_bounced", "lead_assigned", "score_changed"],
  engine: ["job_failed", "job_completed"],
  tasks: ["task_assigned", "task_overdue"],
  team: ["member_invited", "call_limit_reached"],
} as const;

export type NotificationCategory = keyof typeof NOTIFICATION_CATEGORIES;

function stringFromData(
  data: Record<string, unknown>,
  key: string,
): string | null {
  const v = data[key];
  return typeof v === "string" && v.length > 0 ? v : null;
}

function numberFromData(
  data: Record<string, unknown>,
  key: string,
): number | null {
  const v = data[key];
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v && Number.isFinite(Number(v))) return Number(v);
  return null;
}
